import {
  CloseRounded,
  IosShareRounded,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardMedia,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface ProductImageGalleryProps {
  images: string[];
  title: string;
}

const ProductImageGallery: React.FC<ProductImageGalleryProps> = ({
  images,
  title,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedImage, setSelectedImage] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchCurrentX = useRef<number | null>(null);
  const fullscreenItemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const fullscreenSyncedRef = useRef(false);
  const fullscreenScrollLockRef = useRef<{
    scrollY: number;
    bodyOverflow: string;
    bodyPosition: string;
    bodyTop: string;
    bodyWidth: string;
    bodyOverscrollBehavior: string;
    bodyScrollLockAttr: string | null;
    htmlOverflow: string;
    htmlOverscrollBehavior: string;
  } | null>(null);

  const safeImages = Array.isArray(images) ? images : [];
  const imageCount = safeImages.length;

  const handleThumbnailClick = (index: number) => {
    setSelectedImage(index);
  };

  useEffect(() => {
    if (imageCount === 0) return;
    if (selectedImage >= imageCount) {
      setSelectedImage(0);
    }
  }, [imageCount, selectedImage]);

  useEffect(() => {
    if (!isFullscreen) return;

    const body = document.body;
    const html = document.documentElement;
    const scrollY = window.scrollY || window.pageYOffset;

    fullscreenScrollLockRef.current = {
      scrollY,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      bodyOverscrollBehavior: body.style.overscrollBehavior,
      bodyScrollLockAttr: body.getAttribute("data-scroll-lock"),
      htmlOverflow: html.style.overflow,
      htmlOverscrollBehavior: html.style.overscrollBehavior,
    };

    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overscrollBehavior = "contain";
    body.setAttribute("data-scroll-lock", "true");
    html.style.overflow = "hidden";
    html.style.overscrollBehavior = "contain";

    return () => {
      const previous = fullscreenScrollLockRef.current;
      if (!previous) return;

      body.style.overflow = previous.bodyOverflow;
      body.style.position = previous.bodyPosition;
      body.style.top = previous.bodyTop;
      body.style.width = previous.bodyWidth;
      body.style.overscrollBehavior = previous.bodyOverscrollBehavior;
      if (previous.bodyScrollLockAttr === null) {
        body.removeAttribute("data-scroll-lock");
      } else {
        body.setAttribute("data-scroll-lock", previous.bodyScrollLockAttr);
      }
      html.style.overflow = previous.htmlOverflow;
      html.style.overscrollBehavior = previous.htmlOverscrollBehavior;
      window.scrollTo(0, previous.scrollY);
      fullscreenScrollLockRef.current = null;
    };
  }, [isFullscreen]);

  useEffect(() => {
    if (!isFullscreen || !isMobile) {
      fullscreenSyncedRef.current = false;
      return;
    }

    if (fullscreenSyncedRef.current) return;
    fullscreenSyncedRef.current = true;

    const target = fullscreenItemRefs.current[selectedImage];
    if (target) {
      target.scrollIntoView({ behavior: "auto", block: "start" });
    }
  }, [isFullscreen, isMobile, selectedImage]);

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
    touchCurrentX.current = event.touches[0].clientX;
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    touchCurrentX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (imageCount <= 1) return;
    if (touchStartX.current === null || touchCurrentX.current === null) {
      return;
    }
    const deltaX = touchStartX.current - touchCurrentX.current;
    const threshold = 40;
    if (Math.abs(deltaX) > threshold) {
      setSelectedImage((prev) =>
        deltaX > 0 ? (prev + 1) % imageCount : (prev - 1 + imageCount) % imageCount
      );
    }
    touchStartX.current = null;
    touchCurrentX.current = null;
  };

  const showThumbnails = !isMobile && imageCount > 1;
  const showDots = isMobile && imageCount > 1;
  const aspectRatioValue = "1 / 1";

  if (imageCount === 0) {
    return (
      <Card
        sx={{
          height: 400,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          className="notranslate"
          translate="no"
        >
          {t("products.noImagesAvailable")}
        </Typography>
      </Card>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Card
        sx={{
          position: "relative",
          aspectRatio: aspectRatioValue,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "grey.200",
          borderRadius: 3,
          bgcolor: "white",
          boxShadow: "0 20px 50px rgba(15, 23, 42, 0.08)",
          touchAction: "pan-y",
          cursor: "zoom-in",
        }}
        onClick={() => setIsFullscreen(true)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(circle at top, rgba(255,255,255,0.85), rgba(248,250,252,0) 55%)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            display: "flex",
            height: "100%",
            transform: `translateX(-${selectedImage * 100}%)`,
            transition: "transform 0.35s ease",
          }}
        >
          {safeImages.map((image, index) => (
            <Box
              key={`${image}-${index}`}
              sx={{
                minWidth: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "white",
              }}
            >
              <CardMedia
                component="img"
                image={image}
                alt={`${title} - ${index + 1}`}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  transition: "transform 0.35s ease",
                }}
                draggable={false}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "/logo192.png";
                }}
              />
            </Box>
          ))}
        </Box>

        {/* Image Actions */}
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            display: "flex",
            gap: 1,
          }}
        >
          <Tooltip title={t("products.share")}>
            <IconButton
              size="small"
              onClick={async (event) => {
                event.stopPropagation();
                try {
                  if (navigator.share) {
                    await navigator.share({
                      title: title,
                      url: window.location.href,
                    });
                  } else {
                    await navigator.clipboard.writeText(window.location.href);
                  }
                } catch (error: any) {
                  if (
                    error?.name === "AbortError" ||
                    error?.message === "Share canceled"
                  ) {
                    return;
                  }
                  try {
                    await navigator.clipboard.writeText(window.location.href);
                  } catch (_fallbackError) {
                    // Ignore clipboard failures.
                  }
                }
              }}
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.28)",
                color: "#0f172a",
                border: "1px solid rgba(255, 255, 255, 0.45)",
                boxShadow: "0 12px 24px rgba(15, 23, 42, 0.16)",
                backdropFilter: "blur(12px) saturate(140%)",
                "&:hover": {
                  bgcolor: "rgba(255, 255, 255, 0.4)",
                },
              }}
            >
              <IosShareRounded sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Image Counter */}
        <Chip
          label={`${selectedImage + 1}/${imageCount}`}
          size="small"
          sx={{
            position: "absolute",
            bottom: 8,
            right: 8,
            px: 0.6,
            height: 24,
            fontSize: "0.7rem",
            fontWeight: 600,
            bgcolor: "rgba(255, 255, 255, 0.35)",
            color: "#0f172a",
            border: "1px solid rgba(255, 255, 255, 0.5)",
            boxShadow: "0 10px 20px rgba(15, 23, 42, 0.14)",
            backdropFilter: "blur(10px) saturate(140%)",
            "& .MuiChip-label": { px: 0.6 },
          }}
          className="notranslate"
          translate="no"
        />
      </Card>

      {showDots && (
        <Box
          sx={{
            mt: 1.5,
            display: "flex",
            justifyContent: "center",
            gap: 0.75,
          }}
        >
          {safeImages.map((_image, index) => (
            <Box
              key={`dot-${index}`}
              onClick={() => handleThumbnailClick(index)}
              sx={{
                width: selectedImage === index ? 20 : 8,
                height: 8,
                borderRadius: 999,
                cursor: "pointer",
                transition: "all 0.2s ease",
                bgcolor:
                  selectedImage === index ? "primary.main" : "grey.300",
              }}
            />
          ))}
        </Box>
      )}

      {showThumbnails && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            gap: 1,
            justifyContent: "center",
            flexWrap: "wrap",
            p: 1,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "grey.200",
            backgroundColor: "#fff",
            boxShadow: "0 8px 16px rgba(15, 23, 42, 0.05)",
          }}
        >
          {safeImages.slice(0, 8).map((image, index) => (
            <Paper
              key={`thumb-${index}`}
              onClick={() => handleThumbnailClick(index)}
              sx={{
                width: 72,
                height: 72,
                cursor: "pointer",
                border: selectedImage === index ? 2 : 1,
                borderColor:
                  selectedImage === index ? "primary.main" : "grey.300",
                overflow: "hidden",
                transition: "all 0.2s ease-in-out",
                borderRadius: 2,
                "&:hover": {
                  borderColor: "primary.main",
                  boxShadow: "0 8px 18px rgba(15, 23, 42, 0.12)",
                },
              }}
            >
              <CardMedia
                component="img"
                image={image}
                alt={`${title} thumbnail ${index + 1}`}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "/logo192.png";
                }}
              />
            </Paper>
          ))}
        </Box>
      )}

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <Box
          onClick={() => setIsFullscreen(false)}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: isMobile
              ? "#f8fafc"
              : "rgba(0, 0, 0, 0.9)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: isMobile ? "default" : "zoom-out",
            overscrollBehavior: "contain",
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isMobile ? (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  overflowY: "auto",
                  backgroundColor: "#f8fafc",
                  overscrollBehavior: "contain",
                }}
              >
                {safeImages.map((image, index) => (
                  <Box
                    key={`fullscreen-${index}`}
                    sx={{
                      px: 2,
                      py: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      scrollMarginTop: 24,
                    }}
                    ref={(node: HTMLDivElement | null) => {
                      fullscreenItemRefs.current[index] = node;
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={image}
                      alt={`${title} - ${index + 1}`}
                      sx={{
                        width: "100%",
                        height: "auto",
                        maxWidth: "100%",
                        objectFit: "contain",
                        borderRadius: 2,
                        boxShadow: "0 18px 36px rgba(15, 23, 42, 0.12)",
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = "/logo192.png";
                      }}
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  px: { xs: 2, md: 6 },
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    maxHeight: "85vh",
                  }}
                >
                  <CardMedia
                    component="img"
                    image={safeImages[selectedImage]}
                    alt={`${title} - ${selectedImage + 1}`}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "/logo192.png";
                    }}
                  />
                </Box>
                {imageCount > 1 && (
                  <Box
                    sx={{
                      width: 120,
                      maxHeight: "80vh",
                      overflowY: "auto",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      pr: 1,
                    }}
                  >
                    {safeImages.map((image, index) => (
                      <Paper
                        key={`fullscreen-thumb-${index}`}
                        onClick={() => handleThumbnailClick(index)}
                        sx={{
                          width: "100%",
                          aspectRatio: "1 / 1",
                          cursor: "pointer",
                          border: selectedImage === index ? 2 : 1,
                          borderColor:
                            selectedImage === index
                              ? "primary.main"
                              : "rgba(255,255,255,0.3)",
                          overflow: "hidden",
                          borderRadius: 2,
                          bgcolor: "rgba(255,255,255,0.08)",
                          "&:hover": {
                            borderColor: "primary.main",
                          },
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={image}
                          alt={`${title} thumbnail ${index + 1}`}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = "/logo192.png";
                          }}
                        />
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>
            )}
            <IconButton
              onClick={() => setIsFullscreen(false)}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                bgcolor: "rgba(255, 255, 255, 0.28)",
                color: "#0f172a",
                border: "1px solid rgba(255, 255, 255, 0.45)",
                boxShadow: "0 12px 24px rgba(15, 23, 42, 0.16)",
                backdropFilter: "blur(12px) saturate(140%)",
                "&:hover": { bgcolor: "rgba(255, 255, 255, 0.4)" },
              }}
            >
              <CloseRounded sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ProductImageGallery;
